import {
  CircularProgress,
  SVGIcon,
  SectionSubtitle,
} from "@/components";
import { createFinancialData } from "@/api/mappers/hsp/api/mapFinancial";
import type { Evaluation, EquipmentData } from "@/api/mappers/hsp/api/report";
import clsx from "clsx";
import { formatPrice } from "@/helpers/helpers";

export interface HSPFinancialSpecsProps {
  evaluation?: Evaluation;
  equipment?: EquipmentData;
  onCheckDetails?: () => void;
  className?: string;
}

/**
 * Komponent wyświetlający dane finansowe pojazdu HSP
 *
 * @example
 * ```tsx
 * <HSPFinancialSpecs
 *   evaluation={reportData.evaluation}
 *   equipment={reportData.equipment}
 *   onCheckDetails={() => console.log('Check details')}
 * />
 * ```
 */
export const HSPFinancialSpecs: React.FC<HSPFinancialSpecsProps> = ({
  evaluation,
  equipment,
  className,
}) => {
  const financialData = createFinancialData(evaluation, equipment);

  // Jeśli nie ma danych finansowych, nie wyświetlaj komponentu
  if (!financialData) {
    return null;
  }

  return (
    <div className={clsx("mt-20", className)}>
      {/* Nagłówek */}
      <SectionSubtitle
        title="Dane finansowe"
      />

      {/* Główna zawartość */}
      <div className="px-6 pb-6 mt-11">
        {/* Górna sekcja - Cena bazowa */}
        <div className="mb-8">
          <div className="flex items-center gap-[18px]">
            <div className="bg-primary flex h-[46px] w-[46px] items-center justify-center rounded-[10px] p-1">
              <SVGIcon
                name="price-tag"
                fill="white"
                aria-label="Ikona ceny bazowej"
              />
            </div>
            <div className="flex flex-col gap-[10px]">
              <span className="text-grayText text-base font-normal leading-[14px]">
                Cena bazowa
              </span>
              <span className="text-text-primary text-[19px] font-extrabold leading-[20px]">
                {formatPrice(financialData.basePrice)} PLN
              </span>
            </div>
          </div>
        </div>

        {/* Dolna sekcja z tłem */}
        <div className="rounded-card border-gray-300 relative min-h-[190px] border bg-white">
          {/* Grid z trzema kolumnami */}
          <div className="grid relative grid-cols-1 gap-8 xl:grid-cols-3 xl:gap-0">
            {/* Lewa kolumna - Pakiety wyposażenia */}
            <div className="xl:pr-8">
              <div className="flex items-center gap-[14px] px-6 pb-2 pt-6">
                <div className="bg-primary-100 flex h-9 w-9 items-center justify-center rounded-[10px] p-1">
                  <SVGIcon
                    name="file-plus"
                    size={20}
                    fill="primary"
                    aria-label="Ikona pakietów wyposażenia"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-grayText text-sm font-normal leading-[14px]">
                    Pakiety wyposażenia
                  </span>
                  <span className="text-text-primary mt-[4px] text-base font-extrabold leading-[20px]">
                    {formatPrice(financialData.optionalPackagesPrice)} PLN
                  </span>
                </div>
              </div>

              {/* Strzałka i lista pakietów */}
              <div className="px-6 py-2">
                {financialData.optionalPackets
                  .slice(0, 3)
                  .map((equipment) => (
                    <div key={equipment.code} className="ml-8">
                      <span className="text-grayText text-[13px] font-normal">
                        {equipment.name} ({equipment.code}):{" "}
                        {formatPrice(equipment.currentMarketPrice || 0)} PLN
                      </span>
                    </div>
                  ))}
                {financialData.optionalPackets.length > 0 &&
                  financialData.optionalPackets.length < 3 && (
                    <a
                      href="#wyposazenie-opcjonalne"
                      className="text-primary text-[13px] font-normal uppercase tracking-[0.3px] underline"
                    >
                      SPRAWDŹ SZCZEGÓŁY
                    </a>
                  )}
                {financialData.optionalPackets.length >= 3 && (
                  <a
                    href="#wyposazenie-opcjonalne"
                    className="text-primary text-[13px] font-normal uppercase tracking-[0.3px] underline"
                  >
                    + {financialData.optionalEquipments.length - 3}{" "}
                    więcej -
                    SPRAWDŹ SZCZEGÓŁY
                  </a>
                )}
              </div>
            </div>

            {/* Środkowa kolumna - Wyposażenie opcjonalne */}
            <div className="relative p-6 border-t border-gray-300 xl:border-l xl:border-r xl:border-t-0 xl:px-8">
              <div className="absolute left-1/2 top-[0] -translate-y-1/2 xl:left-[-14px] xl:top-1/2">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 14C0 6.26769 6.26769 0 14 0C21.7323 0 28 6.26769 28 14C28 21.7323 21.7323 28 14 28C6.26769 28 0 21.7323 0 14ZM14 7.89744C13.7144 7.89744 13.4405 8.0109 13.2385 8.21286C13.0365 8.41482 12.9231 8.68874 12.9231 8.97436V12.9231H8.97436C8.68874 12.9231 8.41482 13.0365 8.21286 13.2385C8.0109 13.4405 7.89744 13.7144 7.89744 14C7.89744 14.2856 8.0109 14.5595 8.21286 14.7615C8.41482 14.9635 8.68874 15.0769 8.97436 15.0769H12.9231V19.0256C12.9231 19.3113 13.0365 19.5852 13.2385 19.7871C13.4405 19.9891 13.7144 20.1026 14 20.1026C14.2856 20.1026 14.5595 19.9891 14.7615 19.7871C14.9635 19.5852 15.0769 19.3113 15.0769 19.0256V15.0769H19.0256C19.3113 15.0769 19.5852 14.9635 19.7871 14.7615C19.9891 14.5595 20.1026 14.2856 20.1026 14C20.1026 13.7144 19.9891 13.4405 19.7871 13.2385C19.5852 13.0365 19.3113 12.9231 19.0256 12.9231H15.0769V8.97436C15.0769 8.68874 14.9635 8.41482 14.7615 8.21286C14.5595 8.0109 14.2856 7.89744 14 7.89744Z"
                    fill="#20262A"
                  />
                </svg>
              </div>
              <div className="mb-6 flex items-center gap-[14px]">
                <div className="bg-yellow flex h-9 w-9 items-center justify-center rounded-[10px] p-1">
                  <SVGIcon
                    name="trophy"
                    size={22}
                    fill="orange"
                    aria-label="Ikona wyposażenia opcjonalnego"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-grayText text-sm font-normal leading-[14px]">
                    Wyposażenie opcjonalne
                  </span>
                  <span className="text-text-primary mt-[4px] text-base font-extrabold leading-[20px]">
                    {formatPrice(financialData.optionalEquipmentPrice)} PLN
                  </span>
                </div>
              </div>

              {/* Strzałka i tekst */}
              <div className="space-y-4">
                {!financialData.hasOptionalEquipment && (
                  <div className="ml-8">
                    <span className="text-grayText text-[13px] font-normal leading-[15px]">
                      Brak wyposażenia opcjonalnego
                    </span>
                  </div>
                )}
                {financialData.hasOptionalEquipment && (
                  <div>
                    <span className="text-grayText text-[13px] font-normal leading-[15px]">
                      {financialData.optionalEquipments
                        .slice(0, 3)
                        .map((equipment) => (
                          <div
                            key={equipment.code}
                            className="flex gap-2 items-center pb-2"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8 16C9.05057 16 10.0909 15.7931 11.0615 15.391C12.0321 14.989 12.914 14.3997 13.6569 13.6569C14.3997 12.914 14.989 12.0321 15.391 11.0615C15.7931 10.0909 16 9.05057 16 8C16 6.94943 15.7931 5.90914 15.391 4.93853C14.989 3.96793 14.3997 3.08601 13.6569 2.34315C12.914 1.60028 12.0321 1.011 11.0615 0.608964C10.0909 0.206926 9.05057 -1.56548e-08 8 0C5.87827 3.16163e-08 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM7.79378 11.2356L12.2382 5.90222L10.8729 4.76444L7.05067 9.35022L5.07289 7.37156L3.816 8.62844L6.48267 11.2951L7.17067 11.9831L7.79378 11.2356Z"
                                fill="#CA9A39"
                              />
                            </svg>

                            <div className="text-grayText text-[13px] font-normal">
                              {equipment.name} ({equipment.code}):{" "}
                              {formatPrice(equipment.currentMarketPrice || 0)}{" "}
                              PLN
                            </div>
                          </div>
                        ))}
                      {financialData.optionalEquipments.length >= 3 && (
                        <a
                          href="#wyposazenie-opcjonalne"
                          className="text-primary text-[13px] font-normal uppercase tracking-[0.3px] underline"
                        >
                          + {financialData.optionalEquipments.length - 3}{" "}
                          więcej-{" "}
                          SPRAWDŹ SZCZEGÓŁY
                        </a>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Prawa kolumna - Podsumowanie i wykres */}
            <div className="flex relative flex-row gap-2 justify-between p-6 border-t border-gray-300 lg:items-start xl:gap-6 xl:border-t-0 xl:pl-8">
              <div className="absolute left-1/2 top-[0] -translate-y-1/2 xl:left-[-14px] xl:top-1/2 ">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 14C0 6.26818 6.26818 0 14 0C21.7318 0 28 6.26818 28 14C28 21.7318 21.7318 28 14 28C6.26818 28 0 21.7318 0 14ZM7.63636 16.5455C7.63636 16.2079 7.77045 15.8842 8.00914 15.6455C8.24782 15.4068 8.57154 15.2727 8.90909 15.2727H19.0909C19.4285 15.2727 19.7522 15.4068 19.9909 15.6455C20.2295 15.8842 20.3636 16.2079 20.3636 16.5455C20.3636 16.883 20.2295 17.2067 19.9909 17.4454C19.7522 17.6841 19.4285 17.8182 19.0909 17.8182H8.90909C8.57154 17.8182 8.24782 17.6841 8.00914 17.4454C7.77045 17.2067 7.63636 16.883 7.63636 16.5455ZM8.90909 10.1818C8.57154 10.1818 8.24782 10.3159 8.00914 10.5546C7.77045 10.7933 7.63636 11.117 7.63636 11.4545C7.63636 11.7921 7.77045 12.1158 8.00914 12.3545C8.24782 12.5932 8.57154 12.7273 8.90909 12.7273H19.0909C19.4285 12.7273 19.7522 12.5932 19.9909 12.3545C20.2295 12.1158 20.3636 11.7921 20.3636 11.4545C20.3636 11.117 20.2295 10.7933 19.9909 10.5546C19.7522 10.3159 19.4285 10.1818 19.0909 10.1818H8.90909Z"
                    fill="#20262A"
                  />
                </svg>
              </div>
              {/* Łączna wartość */}
              <div className="mb-6">
                <div className="text-grayText mb-2 text-sm font-normal leading-[18px]">
                  Łączna wartość wyposażenia dodatkowego
                </div>
                <div className="text-text-primary text-base font-extrabold leading-[20px]">
                  {formatPrice(financialData.totalAdditionalEquipmentValue)} PLN
                </div>
              </div>

              <div className="relative">
                <CircularProgress
                  percentage={Math.floor(financialData.equipmentPercentage)}
                  size={140}
                  strokeWidth={3}
                  color="#0064FF"
                  backgroundColor="#ECF1F9"
                />
                {/* Percentage in center */}
                <div className="flex absolute inset-0 flex-col justify-center items-center">
                  <div className="font-inter text-text-primary">
                    <span className="text-[26px] font-extrabold">
                      {Math.floor(financialData.equipmentPercentage)}
                    </span>
                    <span className="text-base font-extrabold"> %</span>
                  </div>
                  <div className="font-inter text-grayText mt-1 text-[13px] font-normal">
                    ceny bazowej
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
